import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import styled, { keyframes } from 'styled-components';
import { useApiKey } from '../context/ApiKeyContext';
import { sendStructuredMessage, fetchPrompt } from '../utils/anthropicService';
import { generateAvatarUrl } from '../utils/characterGenerator';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 90vh;
  max-width: 800px;
  width: 100%;
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  position: relative;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem;
  background-color: #e91e63;
  color: white;
`;

const Avatar = styled.img`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  margin-right: 1rem;
  background-color: white;
`;

const ProfileInfo = styled.div`
  flex-grow: 1;
`;

const Name = styled.h3`
  margin: 0;
  font-size: 1.2rem;
`;

const Info = styled.p`
  margin: 0;
  font-size: 0.9rem;
  opacity: 0.9;
`;

const Timer = styled.div`
  padding: 0.5rem 1rem;
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 20px;
  font-size: 0.9rem;
`;

const ChatMessages = styled.div`
  flex-grow: 1;
  padding: 1rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background-color: #f5f5f5;
`;

const MessageBubble = styled.div`
  max-width: 70%;
  padding: 0.8rem 1rem;
  border-radius: 18px;
  position: relative;
  word-wrap: break-word;
  
  ${({ isUser }) => isUser ? `
    align-self: flex-end;
    background-color: #e91e63;
    color: white;
    border-bottom-right-radius: 4px;
  ` : `
    align-self: flex-start;
    background-color: white;
    color: #333;
    border-bottom-left-radius: 4px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  `}
`;

const typingBlink = keyframes`
  0%, 80%, 100% { opacity: 0.3; transform: translateY(0); }
  40% { opacity: 1; transform: translateY(-3px); }
`;

const TypingDots = styled.div`
  display: inline-flex;
  gap: 4px;
  padding: 0.1rem 0;

  span {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background-color: #999;
    animation: ${typingBlink} 1.2s infinite ease-in-out both;
  }

  span:nth-child(2) { animation-delay: 0.15s; }
  span:nth-child(3) { animation-delay: 0.3s; }
`;

const OpenerPrompt = styled.div`
  margin: auto 0 0.5rem;
  text-align: center;
  color: #999;
  font-size: 0.9rem;
`;

const OpenerSuggestions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: auto;
`;

const OpenerChip = styled.button`
  padding: 0.6rem 1rem;
  border: 1px solid #e91e63;
  background-color: white;
  color: #e91e63;
  border-radius: 20px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;

  &:hover {
    background-color: #fff5f8;
  }
`;

const InputArea = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 1rem;
  border-top: 1px solid #eee;
  gap: 0.75rem;
`;

const MessageInput = styled.textarea`
  flex-grow: 1;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  font-family: inherit;
  line-height: 1.4;
  resize: none;
  overflow-y: hidden;
  box-sizing: border-box;
  min-height: 48px;
  max-height: 150px;
`;

// Send and End share one style; Send is the primary (filled) variant, End the
// outline variant, so they read as a matched pair of the same size.
const ChatButton = styled.button`
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid #e91e63;
  background-color: ${({ $primary }) => ($primary ? '#e91e63' : 'white')};
  color: ${({ $primary }) => ($primary ? 'white' : '#e91e63')};

  &:hover {
    background-color: ${({ $primary }) => ($primary ? '#d81b60' : '#fff5f8')};
    border-color: ${({ $primary }) => ($primary ? '#d81b60' : '#e91e63')};
  }

  &:disabled {
    background-color: #ccc;
    border-color: #ccc;
    color: white;
    cursor: not-allowed;
  }
`;

// Shown in place of the input once the chat concludes on its own (time up or,
// in Game Mode, the date being landed) — lets the user read the final message
// before moving on to feedback.
const ConclusionArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  border-top: 1px solid #eee;
`;

const ConclusionText = styled.div`
  font-size: 1rem;
  color: #333;
  text-align: center;
`;

const ContinueButton = styled.button`
  padding: 0.75rem 2rem;
  background-color: #e91e63;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #d81b60;
  }
`;

const MoodDisplay = styled.div`
  display: flex;
  justify-content: center;
  padding: 0.5rem;
  background-color: #f9f9f9;
  border-bottom: 1px solid #eee;
`;

const MoodIndicator = styled.span`
  display: inline-block;
  margin: 0 0.5rem;
  padding: 0.3rem 0.5rem;
  background-color: ${({ active }) => (active ? '#e91e63' : '#f0f0f0')};
  color: ${({ active }) => (active ? 'white' : '#666')};
  border-radius: 4px;
  font-size: 0.8rem;
  cursor: ${({ clickable }) => (clickable ? 'pointer' : 'default')};
  transition: all 0.2s;

  ${({ clickable, active }) => clickable && `
    &:hover {
      background-color: ${active ? '#d81b60' : '#e0e0e0'};
    }
  `}
`;

// Celebratory confetti rendered over the whole viewport (portaled to <body>).
// Three nested layers so the transforms compose without fighting each other:
// the faller drifts straight down, the swayer drifts side to side in a
// sine-like path, and the piece itself spins.
const confettiFall = keyframes`
  from { transform: translateY(0); }
  to { transform: translateY(var(--fall-distance)); }
`;

const confettiSway = keyframes`
  from { transform: translateX(calc(var(--amp) * -1)); }
  to { transform: translateX(var(--amp)); }
`;

const confettiSpin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const ConfettiOverlay = styled.div`
  position: fixed;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  z-index: 9999;
`;

const ConfettiFaller = styled.div`
  position: absolute;
  animation: ${confettiFall} linear forwards;
`;

const ConfettiSwayer = styled.div`
  animation: ${confettiSway} ease-in-out infinite alternate;
`;

const ConfettiPiece = styled.div`
  width: 8px;
  height: 14px;
  border-radius: 2px;
  animation: ${confettiSpin} linear infinite;
`;

const CONFETTI_COLORS = ['#e91e63', '#f06292', '#ffd54f', '#4fc3f7', '#81c784', '#ba68c8'];

// Standard-normal sample (Box–Muller), used to stagger confetti release times.
const gaussian = () => {
  let u = 0;
  let v = 0;
  while (u === 0) u = Math.random();
  while (v === 0) v = Math.random();
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
};

const Confetti = () => {
  // Randomize each piece once per mount: a spread of fall speeds, sway periods
  // and amplitudes, and spin rates/directions, for a natural, non-uniform burst.
  const pieces = useMemo(
    () => Array.from({ length: 400 }, (_, id) => {
      // Every piece begins above the viewport, spread across a tall band up top
      // so they rain in over a few seconds rather than dropping as one line.
      const startAbove = 5 + Math.random() * 95;       // vh above the top edge
      const fallDistance = startAbove + 110;           // travels until ~110vh (below the bottom)
      const speed = 30 + Math.random() * 25;           // vh per second (varied fall speeds)
      return {
        id,
        left: Math.random() * 100,
        color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
        startTop: -startAbove,                         // vh; negative => above the viewport
        fallDistance,
        fallDuration: fallDistance / speed,
        // Stagger when each piece is released — normally distributed over 0–2s,
        // centred at 1s, so the burst eases in and out rather than all at once.
        releaseDelay: Math.min(2, Math.max(0, 1 + gaussian() * 0.5)),
        swayDuration: 0.8 + Math.random() * 1.6,
        swayAmp: 15 + Math.random() * 45,
        spinDuration: 0.6 + Math.random() * 1.6,
        spinReverse: Math.random() < 0.5,
      };
    }),
    []
  );

  if (typeof document === 'undefined') return null;

  return createPortal(
    <ConfettiOverlay aria-hidden="true">
      {pieces.map(p => (
        <ConfettiFaller
          key={p.id}
          style={{
            left: `${p.left}%`,
            top: `${p.startTop}vh`,
            animationDuration: `${p.fallDuration}s`,
            animationDelay: `${p.releaseDelay}s`,
            '--fall-distance': `${p.fallDistance}vh`,
          }}
        >
          <ConfettiSwayer
            style={{
              animationDuration: `${p.swayDuration}s`,
              '--amp': `${p.swayAmp}px`,
            }}
          >
            <ConfettiPiece
              style={{
                backgroundColor: p.color,
                animationDuration: `${p.spinDuration}s`,
                animationDirection: p.spinReverse ? 'reverse' : 'normal',
              }}
            />
          </ConfettiSwayer>
        </ConfettiFaller>
      ))}
    </ConfettiOverlay>,
    document.body
  );
};

// Possible moods for the character
// Ordered negative → positive so the row reads like a mood dial
const MOODS = ['angry', 'condescending', 'suspicious', 'sad', 'bored', 'neutral', 'curious', 'happy', 'excited'];

// Time limit for the chat, in minutes, per mode:
//   Free Practice     — 10 min (relaxed, mood-tweaking practice)
//   Date in Eight     — 8 min  (the timed challenge to land a date)
//   Quick Practice    — 5 min  (a short random run)
const timeLimitMinutes = ({ freeMode, gameMode }) =>
  freeMode ? 10 : gameMode ? 8 : 5;

// Moods that sit "in the middle" — neither clearly warm nor clearly hostile.
// The think-delete-recompose typing animation only plays around these, where a
// character plausibly second-guesses how to respond.
const MIDDLE_MOODS = ['suspicious', 'bored', 'neutral', 'curious'];

// Rough proxy for how much there is to chew on in a message: word count, plus
// extra weight for questions and longer/denser words. Drives both the pre-reply
// delay and whether the recompose animation is allowed to play.
const topicComplexity = (text) => {
  const words = (text.trim().match(/\S+/g) || []).length;
  const questions = (text.match(/\?/g) || []).length;
  const denseWords = (text.match(/[A-Za-z]{8,}/g) || []).length;
  return words + questions * 6 + denseWords * 2;
};

// Past this score a message is "meaty" enough for the recompose animation.
const COMPLEX_TOPIC_SCORE = 18;

const clampMs = (min, max, v) => Math.max(min, Math.min(max, v));

// How long to wait after the user's last message before the character starts
// replying: ~2s for a throwaway line, up to 8s for a dense, multi-question one.
// This is the window in which follow-up messages fold into the same turn.
const responseDelayMs = (text) => clampMs(2000, 8000, 1800 + topicComplexity(text) * 120);

// Dev-only chat command that fires the confetti (see handleSendMessage).
const CONFETTI_COMMAND = '/confetti';
const isLocalhost = () =>
  typeof window !== 'undefined' &&
  ['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname);

// A few opener ideas to help kick off Free Practice — the user can send one
// as-is, tweak it, or ignore them and write their own from scratch.
const getOpenerSuggestions = (character) => {
  const profession = character.profession.toLowerCase();
  const article = /^[aeiou]/i.test(profession) ? 'an' : 'a';

  // Real-world dating advice is consistent: generic greetings ("hey", "how's
  // your week going") get the fewest replies, while openers that reference a
  // specific detail from the person's profile and ask an open-ended question do
  // far better. The one concrete profile detail shown here is the character's
  // profession, so the suggestions lean on that (or invite them to share an
  // interest) rather than opening with a generic question anyone could answer.
  return [
    `Hi ${character.name}! How'd you get into being ${article} ${profession}?`,
    `Hey ${character.name}! What's something you're really into outside of work?`,
    `${character.profession}, nice. What's the part people don't expect about it?`
  ];
};

const ChatInterface = ({ personality, character, onEndChat, onTrialBlocked, conversationId, freeMode = false, gameMode = false }) => {
  const { apiKey, trialMode } = useApiKey();
  const auth = trialMode ? { trial: true } : { apiKey };
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  // True only while a reply is actively being fetched and revealed. During the
  // debounce window between the user's message and that fetch, the input stays
  // open so follow-up texts can fold into the same turn.
  const [isResponding, setIsResponding] = useState(false);
  // While true, an animated "..." bubble stands in for the character composing
  // their reply, for a beat proportional to how long the reply is.
  const [isTyping, setIsTyping] = useState(false);
  const [currentMood, setCurrentMood] = useState('neutral');
  const [timeLeft, setTimeLeft] = useState(timeLimitMinutes({ freeMode, gameMode }) * 60); // In seconds
  const [chatStarted, setChatStarted] = useState(false);
  // Set once the chat ends on its own (time up or a landed date). Holds the
  // banner text, game result, and the messages to hand to the feedback screen,
  // so the user can read the final message before continuing.
  const [conclusion, setConclusion] = useState(null);
  // Incremented to fire a one-off confetti burst from the /confetti dev command;
  // 0 means no dev-triggered confetti. Keyed so each burst restarts fresh.
  const [confettiBurst, setConfettiBurst] = useState(0);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const gameOverRef = useRef(false);
  const typingTimeoutRef = useRef(null);
  const confettiTimeoutRef = useRef(null);
  // Authoritative copy of the displayed messages, so the debounced flush can
  // read the latest conversation without depending on stale render closures.
  const messagesRef = useRef([]);
  // User messages shown but not yet sent to the AI — the current in-progress turn.
  const pendingRef = useRef([]);
  const debounceRef = useRef(null);
  // True from the moment the debounce fires until the reply is revealed; blocks
  // a second flush from racing the first.
  const processingRef = useRef(false);

  // Clear any pending timers if the component unmounts mid-reply/mid-burst.
  useEffect(() => () => {
    clearTimeout(typingTimeoutRef.current);
    clearTimeout(confettiTimeoutRef.current);
    clearTimeout(debounceRef.current);
  }, []);

  // Fire a fresh confetti burst and clear it once the pieces have fallen through.
  const fireConfetti = () => {
    clearTimeout(confettiTimeoutRef.current);
    setConfettiBurst(n => n + 1);
    confettiTimeoutRef.current = setTimeout(() => setConfettiBurst(0), 9500);
  };

  // Pause the chat on its final state with a "See Feedback" button, rather than
  // jumping straight to feedback. `result` is the Game Mode outcome.
  const concludeChat = (finalMessages, result, banner) => {
    if (gameOverRef.current) return;
    gameOverRef.current = true;
    clearTimeout(debounceRef.current);
    setConclusion({ finalMessages, result, banner });
  };

  // Advance to the feedback screen once the user has read the last message.
  const handleContinue = () => {
    if (onEndChat) {
      onEndChat(conclusion.finalMessages, gameMode ? conclusion.result : null);
    }
  };

  // Timer countdown
  useEffect(() => {
    if (!chatStarted || conclusion) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [chatStarted, conclusion]);
  
  // Auto-scroll to the bottom when messages change or the typing bubble appears
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  // Return focus to the message box once a reply finishes (input re-enables)
  useEffect(() => {
    if (!isResponding) {
      inputRef.current?.focus();
    }
  }, [isResponding]);

  // Grow the message box to fit its content, and shrink it back down when the
  // text is sent/cleared. Only show a scrollbar once it hits the max height —
  // below that the box just resizes, so no scrollbar is ever visible normally.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const maxHeight = 150;
    // scrollHeight excludes the border, but height is border-box, so add the
    // vertical borders back or the last line gets clipped by a couple pixels.
    const style = window.getComputedStyle(el);
    const borderY = parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth);
    const fullHeight = el.scrollHeight + borderY;
    if (fullHeight > maxHeight) {
      el.style.height = `${maxHeight}px`;
      el.style.overflowY = 'auto';
    } else {
      el.style.height = `${fullHeight}px`;
      el.style.overflowY = 'hidden';
    }
  }, [input]);
  
  // Format time remaining as MM:SS
  const formatTimeLeft = () => {
    const minutes = Math.floor(timeLeft / 60);
    const seconds = timeLeft % 60;
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };
  
  // What the server needs to build this conversation's system prompt. Fixed for
  // the life of the conversation (same character, same mode) — mood is
  // deliberately kept out so the prompt stays byte-identical and cacheable.
  const promptSpec = useMemo(
    () => ({
      kind: 'chat',
      personalityId: personality.id,
      difficulty: personality.difficulty,
      character,
      freeMode,
      gameMode,
    }),
    [personality, character, freeMode, gameMode]
  );

  // Free Practice lets the user steer the mood mid-conversation, so that note
  // rides on the latest user turn rather than the cached system prompt. The
  // template comes from the server with the rest of the prompt; if the fetch
  // fails we simply send the turn without the note.
  const [moodTemplate, setMoodTemplate] = useState(null);
  useEffect(() => {
    if (!freeMode) return;
    let cancelled = false;
    fetchPrompt(promptSpec)
      .then((prompt) => { if (!cancelled) setMoodTemplate(prompt.moodTemplate || null); })
      .catch((err) => console.error('Could not load mood template:', err));
    return () => { cancelled = true; };
  }, [freeMode, promptSpec]);

  // Collapse the displayed bubbles into the API's alternating-role format:
  // consecutive user texts (the ones fired off in one burst) merge into a single
  // turn, the latest user turn carries the mood note in Free Practice, and the
  // final block gets the cache breakpoint that closes the reusable prefix.
  const buildApiMessages = (bubbles) => {
    const merged = [];
    bubbles.forEach((m) => {
      const last = merged[merged.length - 1];
      if (last && last.role === m.role) last.content += `\n${m.content}`;
      else merged.push({ role: m.role, content: m.content });
    });
    return merged.map((m, i) => {
      const isLast = i === merged.length - 1;
      const text =
        isLast && m.role === 'user' && freeMode && moodTemplate
          ? `${m.content}\n\n${moodTemplate.replace('{{mood}}', () => currentMood)}`
          : m.content;
      return isLast
        ? { role: m.role, content: [{ type: 'text', text, cache_control: { type: 'ephemeral' } }] }
        : { role: m.role, content: text };
    });
  };

  // Append a bubble and keep the authoritative ref in sync.
  const pushMessage = (msg) => {
    const next = [...messagesRef.current, msg];
    messagesRef.current = next;
    setMessages(next);
    return next;
  };

  const wait = (ms) => new Promise((resolve) => {
    typingTimeoutRef.current = setTimeout(resolve, ms);
  });

  // Show the "…" typing bubble before revealing the reply. When the topic is
  // meaty and the character's read on it is ambivalent (a middle-of-the-road
  // mood), sometimes type, stop, and start again — as if they thought better of
  // a first draft and rewrote it.
  const playTypingIndicator = async (reply, batchText, mood) => {
    const totalDelay = Math.min(4500, 700 + reply.length * 25);
    const recompose =
      topicComplexity(batchText) >= COMPLEX_TOPIC_SCORE &&
      MIDDLE_MOODS.includes(mood) &&
      Math.random() < 0.15;

    if (recompose) {
      const firstDraft = clampMs(900, 1600, totalDelay * 0.5);
      setIsTyping(true);
      await wait(firstDraft);
      setIsTyping(false); // a beat of nothing — as if the draft got deleted
      await wait(700);
      setIsTyping(true); // starts over
      await wait(Math.max(1000, totalDelay - firstDraft));
      setIsTyping(false);
    } else {
      setIsTyping(true);
      await wait(totalDelay);
      setIsTyping(false);
    }
  };

  // Once the debounce window closes, send everything the user piled up as one
  // turn and reveal the character's reply.
  const flushPending = async () => {
    clearTimeout(debounceRef.current);
    const batch = pendingRef.current;
    if (batch.length === 0 || processingRef.current || gameOverRef.current) return;
    pendingRef.current = [];
    const batchText = batch.join('\n');

    processingRef.current = true;
    setIsResponding(true);
    try {
      const response = await sendStructuredMessage(
        auth,
        promptSpec,
        buildApiMessages(messagesRef.current),
        { includeDateAccepted: gameMode, conversationId }
      );

      // Trial ran out or the owner is out of credits: bail out to the home
      // screen, which shows the appropriate "use your own key" path.
      if (!response.success && (response.code === 'trial_exhausted' || response.code === 'owner_out_of_credits')) {
        if (onTrialBlocked) onTrialBlocked(response.code);
        return;
      }

      // The clock may have run out (or a date landed) while we were waiting.
      if (gameOverRef.current) return;

      if (response.success) {
        const mood = freeMode ? currentMood : response.mood;
        await playTypingIndicator(response.reply, batchText, mood);
        if (gameOverRef.current) return;

        const updated = pushMessage({ role: 'assistant', content: response.reply });
        if (!freeMode) setCurrentMood(response.mood);
        if (gameMode && response.dateAccepted) {
          concludeChat(updated, 'won', `${character.name} said yes! 🎉`);
        }
      } else {
        console.error('Error sending message:', response.error);
        pushMessage({
          role: 'assistant',
          content: "Sorry, I'm having trouble responding right now. Can we try a different topic?",
        });
      }
    } catch (error) {
      console.error('Error in flushPending:', error);
    } finally {
      processingRef.current = false;
      setIsResponding(false);
      // Anything typed while the reply was in flight starts a fresh turn.
      if (pendingRef.current.length > 0) scheduleFlush();
    }
  };

  // (Re)arm the debounce timer. Each new message pushes the reply back, and the
  // delay scales with everything buffered so far.
  const scheduleFlush = () => {
    if (processingRef.current) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(flushPending, responseDelayMs(pendingRef.current.join('\n')));
  };

  // Sending shows the user's text right away, but the reply is held until they
  // stop typing — so back-to-back messages become a single turn.
  const handleSendMessage = () => {
    if (!input.trim() || isResponding) return;

    // Dev-only cheat: on localhost, "/confetti" fires the celebration instead
    // of being sent as a message.
    if (isLocalhost() && input.trim().toLowerCase() === CONFETTI_COMMAND) {
      setInput('');
      fireConfetti();
      return;
    }

    setChatStarted(true);
    pushMessage({ role: 'user', content: input });
    pendingRef.current.push(input);
    setInput('');
    scheduleFlush();
  };

  // Enter sends; Shift+Enter inserts a newline (the box grows to fit).
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!isResponding) {
        handleSendMessage();
      }
    }
  };
  
  // Handle mood change
  const handleMoodChange = (mood) => {
    setCurrentMood(mood);
  };
  
  // Manually ending goes straight to feedback — it's a deliberate user action,
  // so there's no last message to pause on.
  const handleEndChat = () => {
    if (gameOverRef.current) return;
    gameOverRef.current = true;
    if (onEndChat) {
      onEndChat(messages, gameMode ? 'lost' : null);
    }
  };

  // When time runs out, pause on the final message with a "See Feedback" button
  // rather than jumping away (counts as not winning in Game Mode).
  useEffect(() => {
    if (timeLeft === 0 && chatStarted) {
      concludeChat(messagesRef.current, 'lost', "Time's up!");
    }
  }, [timeLeft, chatStarted]);
  
  return (
    <Container>
      {(conclusion?.result === 'won' || confettiBurst > 0) && <Confetti key={confettiBurst} />}
      <Header>
        <Avatar src={generateAvatarUrl(character, currentMood)} alt={character.name} />
        <ProfileInfo>
          <Name>{character.name}, {character.age}</Name>
          <Info>{character.profession} • {character.nationality}</Info>
        </ProfileInfo>
        <Timer>{formatTimeLeft()}</Timer>
      </Header>
      
      {!gameMode && (
        <MoodDisplay>
          {MOODS.map(mood => (
            <MoodIndicator
              key={mood}
              active={currentMood === mood}
              clickable={freeMode}
              onClick={freeMode ? () => handleMoodChange(mood) : undefined}
            >
              {mood}
            </MoodIndicator>
          ))}
        </MoodDisplay>
      )}

      <ChatMessages>
        {freeMode && messages.length === 0 && (
          <>
            <OpenerPrompt>Not sure how to start? Try one of these, or write your own below.</OpenerPrompt>
            <OpenerSuggestions>
              {getOpenerSuggestions(character).map((suggestion, index) => (
                <OpenerChip
                  key={index}
                  onClick={() => {
                    setInput(suggestion);
                    inputRef.current?.focus();
                  }}
                >
                  {suggestion}
                </OpenerChip>
              ))}
            </OpenerSuggestions>
          </>
        )}
        {messages.map((message, index) => (
          <MessageBubble key={index} isUser={message.role === 'user'}>
            {message.content}
          </MessageBubble>
        ))}
        {isTyping && (
          <MessageBubble isUser={false}>
            <TypingDots aria-label={`${character.name} is typing`}>
              <span /><span /><span />
            </TypingDots>
          </MessageBubble>
        )}
        <div ref={messagesEndRef} />
      </ChatMessages>
      
      {conclusion ? (
        <ConclusionArea>
          <ConclusionText>{conclusion.banner}</ConclusionText>
          <ContinueButton onClick={handleContinue}>See Feedback</ContinueButton>
        </ConclusionArea>
      ) : (
        <InputArea>
          <MessageInput
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type your message..."
            rows={1}
            disabled={isResponding}
          />
          <ChatButton $primary onClick={handleSendMessage} disabled={isResponding || !input.trim()}>
            {isResponding ? 'Sending...' : 'Send'}
          </ChatButton>
          <ChatButton onClick={handleEndChat}>End</ChatButton>
        </InputArea>
      )}
    </Container>
  );
};

export default ChatInterface;
